import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { OnboardingProgressData } from '../types/api';
import { isSocialLogin, isEmailOtpVerified, setEmailOtpVerified, getCurrentUserEmail, obSet, OB_KEYS } from '../utils/onboardingStorage';
import { restoreOnboardingState } from '../utils/restoreOnboardingState';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import { performEmergencyLogout } from '../utils/logout';
import SkeletonLoader from '../components/ui/SkeletonLoader';
import { useDispatch } from 'react-redux';

interface OnboardingGuardOptions {
  enabled?: boolean;
  redirectOnComplete?: string;
  showNotifications?: boolean;
}

interface UseOnboardingGuardReturn {
  isLoading: boolean;
  progress: OnboardingProgressData | null;
  canAccessCurrentRoute: boolean;
  redirectToNextStep: () => void;
  refreshProgress: () => Promise<void>;
  getPathFromStepId: (stepId: number) => string;
}

/**
 * Hook to guard onboarding routes and enforce sequential step completion
 * 
 * Features:
 * - Checks progress on route changes
 * - Redirects to next incomplete step if user tries to access wrong step
 * - Prevents access to incomplete steps
 * - Allows navigation to completed steps only
 * 
 * Requirements covered: 1.1, 1.5, 2.1, 2.2, 2.3
 */
export const useOnboardingGuard = (options: OnboardingGuardOptions = {}): UseOnboardingGuardReturn => {
  const {
    enabled = true,
    redirectOnComplete = '/',
    showNotifications = true
  } = options;

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState<OnboardingProgressData | null>(() => {
    try {
      const cached = localStorage.getItem('onboarding_cached_progress');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [canAccessCurrentRoute, setCanAccessCurrentRoute] = useState(true);

  // Navigation lock to prevent concurrent redirects
  const isNavigatingRef = useRef(false);
  const navigationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const progressRef = useRef<OnboardingProgressData | null>(null);

  // Keep progressRef in sync
  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  // Safe navigation helper — prevents multiple rapid redirects
  const safeNavigate = useCallback((path: string, options?: { replace?: boolean }) => {
    if (isNavigatingRef.current) return;
    isNavigatingRef.current = true;
    navigate(path, options);
    // Reset the lock after a short delay to allow subsequent navigations
    if (navigationTimerRef.current) clearTimeout(navigationTimerRef.current);
    navigationTimerRef.current = setTimeout(() => {
      isNavigatingRef.current = false;
    }, 500);
  }, [navigate]);

  // Map route paths to step IDs
  const getStepIdFromPath = useCallback((pathname: string): number | null => {
    const stepRoutes: Record<string, number> = {
      '/onboarding/basic-details': 1,
      '/onboarding/company-details': 2,
      '/onboarding/company-intro': 3,
      '/onboarding/verification': 4,
      '/onboarding/activate': 5,
      '/onboarding/credits': 5
    };
    
    return stepRoutes[pathname] || null;
  }, []);

  // Map step IDs to route paths
  const getPathFromStepId = useCallback((stepId: number): string => {
    const stepPaths: Record<number, string> = {
      1: '/onboarding/basic-details',
      2: '/onboarding/company-details',
      3: '/onboarding/company-intro',
      4: '/onboarding/verification',
      5: '/onboarding/activate'
    };
    
    return stepPaths[stepId] || '/onboarding/basic-details';
  }, []);

  // Check if user can access a specific step
  const canAccessStep = useCallback((stepId: number, progressData: OnboardingProgressData): boolean => {
    if (!progressData || !progressData.steps) return false;

    // Can access all steps if onboarding is complete
    if (progressData.is_complete) return true;

    const step = progressData.steps.find(s => s.step_id === stepId);
    if (!step) return false;

    // Can access if step is completed (status_id === 1)
    if (step.status_id === 1) return true;

    // Can access if it's the next incomplete step
    if (stepId === progressData.next_incomplete_step) return true;

    // Can access if all previous steps before this step are completed
    const allPreviousCompleted = progressData.steps
      .filter(s => s.step_id < stepId)
      .every(s => s.status_id === 1);
    if (allPreviousCompleted) return true;

    // Can access first step always
    if (stepId === 1) return true;

    return false;
  }, []);

  // Get next incomplete step path
  const getNextIncompleteStepPath = useCallback((progressData: OnboardingProgressData): string => {
    if (progressData.is_complete) {
      return redirectOnComplete;
    }

    if (progressData.next_incomplete_step) {
      return getPathFromStepId(progressData.next_incomplete_step);
    }

    // Fallback to first step
    return '/onboarding/basic-details';
  }, [redirectOnComplete, getPathFromStepId]);

  // Check if user is authenticated and has a token
  const isAuthenticated = useCallback(() => {
    const token = localStorage.getItem("authToken") || localStorage.getItem("token") || sessionStorage.getItem("authToken") || sessionStorage.getItem("token");
    return !!token;
  }, []);

  // Check if this is a new user signup flow
  const isNewUserSignup = useCallback(() => {
    // Check for indicators that this is a new user
    const socialType = localStorage.getItem('social_type');
    const loginWithGoogle = localStorage.getItem('loginWithGoogle');
    const hasBasicDetails = localStorage.getItem('onboarding_BasicDetail');

    const hasAuthToken = localStorage.getItem("authToken") || localStorage.getItem("token") || sessionStorage.getItem("authToken") || sessionStorage.getItem("token");

    const isNewUser = (socialType || loginWithGoogle) && !hasBasicDetails && !hasAuthToken;

    console.log('🔍 New user check:', {
      hasSocialType: !!socialType,
      hasLoginWithGoogle: !!loginWithGoogle,
      hasBasicDetails: !!hasBasicDetails,
      hasAuthTokenLocalStorage: !!hasAuthToken,
      hasAuthToken: !!hasAuthToken,
      isNewUser
    });

    return isNewUser;
  }, []);

  // Fetch progress data from API
  const fetchProgress = useCallback(async (showSkeleton = true): Promise<OnboardingProgressData | null> => {
    // Don't fetch progress for unauthenticated users or new signups
    if (!isAuthenticated()) {
      return null;
    }

    // For new user signups, don't fetch progress yet
    if (isNewUserSignup()) {
      return null;
    }

    try {
      if (showSkeleton) {
        setIsLoading(true);
      }
      const response = await apiService.getSignupProgress();
      
      if (response.status && response.data) {
        try {
          localStorage.setItem('onboarding_cached_progress', JSON.stringify(response.data));
        } catch (e) {
          console.warn('Failed to cache onboarding progress:', e);
        }
        return response.data;
      } else {
        console.error('Invalid progress response:', response);
        return null;
      }
    } catch (error: any) {
      console.error('Failed to fetch onboarding progress:', error);
      
      // Handle session expiry gracefully
      if (error.response?.status === 401 || error.response?.status === 403) {
        // Clean up ALL auth-related storage to avoid half-authenticated states
        localStorage.removeItem('authToken');
        localStorage.removeItem('token');
        localStorage.removeItem('auth_token');
        Cookies.remove('authToken');
        localStorage.removeItem('onboarding_cached_progress');

        if (showNotifications) {
          toast.error('Session expired. Please log in to resume onboarding.');
        }
        performEmergencyLogout();
        return null;
      }
      
      // Handle specific error cases - but don't show errors for new users
      if (error.response?.status === 404) {
        return null;
      } else if (error.message?.includes('Network error')) {
        if (showNotifications) {
          toast.error('Unable to load progress. Please check your connection.');
        }
      } else if (error.response?.status >= 500) {
        if (showNotifications) {
          toast.error('Server error. Please try again later.');
        }
      }
      
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [showNotifications]);

  // Refresh progress data
  const refreshProgress = useCallback(async (): Promise<void> => {
    const progressData = await fetchProgress(false);
    if (progressData) {
      setProgress(progressData);
    }
  }, [fetchProgress]);

  // Redirect to next incomplete step
  const redirectToNextStep = useCallback(() => {
    const currentProgress = progressRef.current;
    if (!currentProgress) return;

    const nextStepPath = getNextIncompleteStepPath(currentProgress);
    
    if (nextStepPath !== location.pathname) {
      safeNavigate(nextStepPath, { replace: true });
    }
  }, [location.pathname, safeNavigate, getNextIncompleteStepPath]);

  // Check route access and redirect if necessary
  const checkRouteAccess = useCallback(async () => {
    if (!enabled) {
      setCanAccessCurrentRoute(true);
      return;
    }

    // Skip check for non-onboarding routes
    if (!location.pathname.startsWith('/onboarding/')) {
      setCanAccessCurrentRoute(true);
      return;
    }

    // Skip if a navigation is already in progress (prevents flickering loops)
    if (isNavigatingRef.current) {
      return;
    }

    const currentStepId = getStepIdFromPath(location.pathname);
    
    // If we can't determine the step ID, allow access (might be a valid onboarding route)
    if (currentStepId === null) {
      setCanAccessCurrentRoute(true);
      return;
    }

    // For new users or unauthenticated users, allow access to basic details
    if (!isAuthenticated() || isNewUserSignup()) {
      if (currentStepId === 1) {
        // Allow access to basic details for new users
        setCanAccessCurrentRoute(true);
        return;
      } else {
        // Redirect new users to basic details if they try to access other steps
        setCanAccessCurrentRoute(false);
        safeNavigate('/onboarding/basic-details', { replace: true });
        if (showNotifications) {
          toast.info('Please start with basic details.');
        }
        return;
      }
    }

    // Optimistic check: if we have cached progress, check access immediately (prevents skeleton flicker)
    let activeProgress = progress;
    let didOptimisticCheck = false;
    if (activeProgress) {
      const canAccess = canAccessStep(currentStepId, activeProgress);
      setCanAccessCurrentRoute(canAccess);
      didOptimisticCheck = true;
      if (!canAccess) {
        const nextStepPath = getNextIncompleteStepPath(activeProgress);
        navigate(nextStepPath, { replace: true });
        return;
      }
    }

    // Fetch fresh progress data for authenticated users with existing progress
    const progressData = await fetchProgress(!didOptimisticCheck);
    
    // Re-check navigation lock after async fetch — another effect may have already navigated
    if (isNavigatingRef.current) {
      return;
    }
    
    if (!progressData) {
      // If we can't get progress data for authenticated user, allow basic details
      if (currentStepId === 1) {
        setCanAccessCurrentRoute(true);
        return;
      } else {
        // Redirect to first step if no progress data
        setCanAccessCurrentRoute(false);
        safeNavigate('/onboarding/basic-details', { replace: true });
        return;
      }
    }

    setProgress(progressData);

    const userEmail = getCurrentUserEmail();
    const isBackendEmailVerified =
      progressData.email_verified === true ||
      (progressData as any).email_verified === 1 ||
      (progressData as any).email_verified === '1';

    if (isBackendEmailVerified && userEmail) {
      setEmailOtpVerified(userEmail, true);
      obSet(OB_KEYS.EMAIL_VERIFIED);
    }
    const isEmailVerified = isEmailOtpVerified(userEmail) || isBackendEmailVerified;

    restoreOnboardingState(progressData, userEmail, dispatch);

    // If email is not verified, they must be on basic details (step 1) or email-otp.
    // If they try to access any other step, redirect to email-otp.
    // Social logins are exempt - the provider has already verified their email.
    if (!isEmailVerified && !isSocialLogin()) {
      if (location.pathname !== '/onboarding/basic-details' && location.pathname !== '/onboarding/email-otp') {
        setCanAccessCurrentRoute(false);
        // Ensure email is populated in localStorage for OTP page
        const storedEmail = userEmail || localStorage.getItem('email');
        if (storedEmail) {
          try {
            localStorage.setItem('tnb_otp_email', JSON.parse(storedEmail));
          } catch {
            localStorage.setItem('tnb_otp_email', storedEmail);
          }
        }
        navigate('/onboarding/email-otp', { replace: true });
        if (showNotifications && !didOptimisticCheck) {
          toast.warning('Please verify your email first.');
        }
        return;
      }
    }

    // If onboarding is complete and email is verified (or social), redirect to main app
    if (progressData.is_complete && (isEmailVerified || isSocialLogin())) {
      setCanAccessCurrentRoute(false);
      safeNavigate(redirectOnComplete, { replace: true });
      return;
    }

    // If email is verified, they should not access email-otp. Redirect them to the next incomplete step.
    if ((isEmailVerified || isSocialLogin()) && location.pathname === '/onboarding/email-otp') {
      setCanAccessCurrentRoute(false);
      const targetPath = getNextIncompleteStepPath(progressData);
      safeNavigate(targetPath, { replace: true });
      return;
    }

    // Check if user can access current step
    const canAccess = canAccessStep(currentStepId, progressData);
    setCanAccessCurrentRoute(canAccess);

    if (!canAccess) {
      // Redirect to next incomplete step
      const nextStepPath = getNextIncompleteStepPath(progressData);
      
      if (showNotifications) {
        const nextStep = progressData.steps.find(s => s.step_id === progressData.next_incomplete_step);
        const stepName = nextStep?.step_name || 'next step';
        toast.warning(`Please complete "${stepName}" first.`);
      }
      
      safeNavigate(nextStepPath, { replace: true });
    }
  }, [
    enabled,
    location.pathname,
    getStepIdFromPath,
    isAuthenticated,
    isNewUserSignup,
    fetchProgress,
    canAccessStep,
    getNextIncompleteStepPath,
    safeNavigate,
    redirectOnComplete,
    showNotifications,
    progress
  ]);

  // Effect to check route access on location change — use pathname as only
  // effect trigger so we don't re-run on every callback recreation.
  useEffect(() => {
    // Small debounce to let React finish mounting the new page before checking
    const timer = setTimeout(() => {
      checkRouteAccess();
    }, 50);
    return () => clearTimeout(timer);
  }, [location.pathname, enabled]);

  // Initial progress fetch — only on mount or when enabled/path changes
  useEffect(() => {
    if (enabled && location.pathname.startsWith('/onboarding/')) {
      refreshProgress();
    }
  }, [enabled, location.pathname]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (navigationTimerRef.current) clearTimeout(navigationTimerRef.current);
    };
  }, []);

  return {
    isLoading,
    progress,
    canAccessCurrentRoute,
    redirectToNextStep,
    refreshProgress,
    getPathFromStepId
  };
};

/**
 * Higher-order component to wrap onboarding pages with navigation guard
 */
export const withOnboardingGuard = <P extends object>(
  Component: React.ComponentType<P>,
  options?: OnboardingGuardOptions
) => {
  return (props: P) => {
    const { isLoading, canAccessCurrentRoute } = useOnboardingGuard(options);

    // Show loading state while checking access
    if (isLoading) {
      return React.createElement(
        'div',
        {
          className: 'd-flex justify-content-center align-items-center',
          style: { minHeight: '50vh' }
        },
        React.createElement(SkeletonLoader, {
          inline: true,
          width: 24,
          height: 24,
          style: { borderRadius: 8 }
        })
      );
    }

    // Don't render component if user can't access this route
    if (!canAccessCurrentRoute) {
      return null;
    }

    return React.createElement(Component, props);
  };
};

/**
 * Hook for step navigation with validation
 */
export const useStepNavigation = () => {
  const { progress, refreshProgress } = useOnboardingGuard();
  const navigate = useNavigate();

  const navigateToStep = useCallback((stepId: number, progressData?: OnboardingProgressData | null) => {
    const currentProgress = progressData || progress;

    if (!currentProgress) {
      // For new users without progress data, allow basic navigation
      if (stepId === 1) {
        navigate('/onboarding/basic-details');
        return true;
      } else {
        toast.warning('Please complete basic details first.');
        navigate('/onboarding/basic-details');
        return false;
      }
    }

    const step = currentProgress.steps.find(s => s.step_id === stepId);
    if (!step) {
      toast.error('Invalid step.');
      return false;
    }

    // Check if step is accessible
    if (step.status_id === 1) {
      // Step is completed - allow navigation
      navigate(step.route_path);
      return true;
    } else if (stepId === currentProgress.next_incomplete_step) {
      // This is the next step to complete - allow navigation
      navigate(step.route_path);
      return true;
    } else {
      // Step is locked
      toast.warning(`Please complete previous steps first.`);
      return false;
    }
  }, [progress, navigate]);

  const navigateToNextStep = useCallback((progressData?: OnboardingProgressData | null) => {
    const currentProgress = progressData || progress;

    if (!currentProgress || currentProgress.is_complete) return false;

    if (currentProgress.next_incomplete_step) {
      return navigateToStep(currentProgress.next_incomplete_step, currentProgress);
    }

    return false;
  }, [progress, navigateToStep]);

  const navigateToPreviousStep = useCallback((progressData?: OnboardingProgressData | null) => {
    const currentProgress = progressData || progress;

    if (!currentProgress) return false;

    const currentStepId = currentProgress.next_incomplete_step;
    if (!currentStepId || currentStepId <= 1) return false;

    // Find the previous completed step
    const sortedSteps = currentProgress.steps
      .filter(s => s.step_id < currentStepId && s.status_id === 1)
      .sort((a, b) => b.step_id - a.step_id);

    if (sortedSteps.length > 0) {
      return navigateToStep(sortedSteps[0].step_id, currentProgress);
    }

    return false;
  }, [progress, navigateToStep]);

  // New function: refresh progress and then navigate to next step
  const refreshAndNavigateToNextStep = useCallback(async () => {
    try {
      // Clear cache first to ensure fresh data
      apiService.clearProgressCache();

      // Fetch the latest progress data directly from API
      const response = await apiService.getSignupProgress();

      if (response?.status && response?.data) {
        const updatedProgress = response.data;

        // Also update the local progress state
        await refreshProgress();

        // Navigate using the fresh progress data
        if (updatedProgress.is_complete) {
          toast.success('Onboarding complete! Redirecting to dashboard...', { toastId: 'onboarding_complete_toast' });
          navigate('/');
          return true;
        }

        if (updatedProgress.next_incomplete_step) {
          return navigateToStep(updatedProgress.next_incomplete_step, updatedProgress);
        }
      }

      return false;
    } catch (error) {
      console.error('Failed to refresh and navigate:', error);
      return false;
    }
  }, [refreshProgress, navigate, navigateToStep]);

  return {
    progress,
    navigateToStep,
    navigateToNextStep,
    navigateToPreviousStep,
    refreshProgress,
    refreshAndNavigateToNextStep
  };
};